import { Command } from '@/components/atoms/Command';
import { Popover } from '@/components/atoms/Popover';
import { Tooltip } from '@/components/atoms/Tooltip';
import { CheckmarkIcon, Chevron, XIcon } from '@/resources/assets/images';
import { sendAnalytics } from '@/services/analyticsService';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useMemo, useRef, useState } from 'react';

export type ItemsListProps = Array<{
	id: string;
	value: string;
	label: string;
}>;

type ComboboxProps = {
	itemsList: ItemsListProps;
	placeholder: string;
	emptyMessage: string;
	selectedItem: string | undefined;
	onUpdateSelectedItem: (value: string) => void;
	hideClearSelection?: boolean;
};

export const Combobox = ({
	itemsList,
	placeholder,
	emptyMessage,
	selectedItem,
	onUpdateSelectedItem,
	hideClearSelection = false
}: ComboboxProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState('');

	const handleClearSelection = () => {
		onUpdateSelectedItem('');
		sendAnalytics({
			type: 'track',
			eventName: `${placeholder} cleared in Activity reports`
		});
	};

	const handleSelectItem = (currentValue: string) => {
		setSearch('');
		onUpdateSelectedItem(currentValue);
		setIsOpen(false);
		sendAnalytics({
			type: 'track',
			eventName: `${placeholder} selected in Activity reports`,
			properties: {
				selectedValue: currentValue
			}
		});
	};

	const selectedValue = selectedItem
		? itemsList.find(({ value }) => value === selectedItem)?.label
		: placeholder;

	return (
		<Popover.Root open={isOpen} onOpenChange={setIsOpen}>
			<Tooltip.Provider>
				<Tooltip.Root>
					<Tooltip.Trigger asChild>
						<Popover.Trigger className="group flex w-full items-center gap-1 transition-[width] duration-300">
							<p
								data-is-placeholder={!selectedItem}
								className="inline-block w-fit overflow-hidden text-ellipsis text-start text-dm-white transition-[width] duration-300 data-[is-placeholder=true]:text-grey xl:w-56"
							>
								{selectedValue}
							</p>

							{!hideClearSelection && selectedItem ? (
								<button
									type="button"
									onClick={handleClearSelection}
									className="cursor-pointer opacity-50 transition-opacity hover:opacity-100"
								>
									<XIcon className="fill-lightest-grey" />
								</button>
							) : null}

							<Chevron className="block shrink-0 fill-lightest-grey opacity-50" />
						</Popover.Trigger>
					</Tooltip.Trigger>

					<Tooltip.Content>{selectedValue}</Tooltip.Content>
				</Tooltip.Root>
			</Tooltip.Provider>

			<Popover.Content className="min-w-56 p-0">
				<Command.Root
					shouldFilter={false}
					className="flex flex-col items-center justify-center"
				>
					<Command.Input
						placeholder={placeholder}
						className="w-full rounded-none"
						onValueChange={setSearch}
					/>

					<ComboboxList
						isOpen={isOpen}
						search={search}
						itemsList={itemsList}
						emptyMessage={emptyMessage}
						selectedItem={selectedItem}
						onSelectItem={handleSelectItem}
					/>
				</Command.Root>
			</Popover.Content>
		</Popover.Root>
	);
};

type ComboboxListProps = Pick<
	ComboboxProps,
	'itemsList' | 'selectedItem' | 'emptyMessage'
> & {
	search: string;

	isOpen: boolean;
	onSelectItem: (value: string) => void;
};

const ComboboxList = ({
	isOpen,
	search,
	itemsList,
	emptyMessage,
	selectedItem,
	onSelectItem
}: ComboboxListProps) => {
	const parentRef = useRef(null);

	const filteredList = useMemo(() => {
		if (!search) return itemsList;

		return itemsList.filter(
			({ id, label, value }) =>
				id.trim().toLowerCase().includes(search.trim().toLowerCase()) ||
				label
					.trim()
					.toLowerCase()
					.includes(search.trim().toLowerCase()) ||
				value.trim().toLowerCase().includes(search.trim().toLowerCase())
		);
	}, [search, itemsList]);

	const rowVirtualizer = useVirtualizer({
		count: isOpen ? filteredList.length : 0,
		debug: true,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 35,
		overscan: 10
	});

	const virtualizedList = rowVirtualizer.getVirtualItems();

	return (
		<Command.List ref={parentRef} className="relative w-full p-2">
			{isOpen && filteredList.length > 0 ? (
				<Command.Group
					className="relative"
					style={{
						height: `${rowVirtualizer.getTotalSize()}px`
					}}
				>
					{virtualizedList.map(virtualRow => {
						const { id, label, value } =
							filteredList[virtualRow.index];

						return (
							<Command.Item
								key={id}
								value={value}
								onSelect={onSelectItem}
								className="absolute left-0 top-0 w-full"
								style={{
									transform: `translateY(${virtualRow.start}px)`
								}}
							>
								<Tooltip.Provider>
									<Tooltip.Root>
										<Tooltip.Trigger asChild>
											<span className="w-full overflow-hidden text-ellipsis whitespace-nowrap text-start">
												{label}
											</span>
										</Tooltip.Trigger>

										<Tooltip.Content>
											{label}
										</Tooltip.Content>
									</Tooltip.Root>
								</Tooltip.Provider>

								<CheckmarkIcon
									data-is-selected={selectedItem === value}
									className="ml-auto opacity-0 data-[is-selected=true]:opacity-100"
								/>
							</Command.Item>
						);
					})}
				</Command.Group>
			) : (
				<Command.Empty>{emptyMessage}</Command.Empty>
			)}
		</Command.List>
	);
};
